// Workforce Execution API client (Phase 2) — work assignments, assignment
// types, workforce presence, exceptions and reporting. All calls ride the
// gateway (/api/v1/*); RBAC and tenant scoping are server-side.
import { gatewayApi } from '@/lib/hrApi';

const WA = '/api/v1/work-assignments';
const TYPES = '/api/v1/assignment-types';
const PRESENCE = '/api/v1/presence';

export interface AssignmentType {
  id: string;
  key: string;
  label: string;
  category: string;
  presenceLabel: string;
  countsAsPresent: boolean;
  requiresApproval: boolean;
  requiresSite: boolean;
  requiresContext: boolean;
  requiresTimes: boolean;
  allowsSelfCreate: boolean;
  checkInPolicy: 'none' | 'optional' | 'required';
  checkOutRequired: boolean;
  gpsPolicy: 'off' | 'checkin_only' | 'checkin_and_checkout';
  geofencePolicy: 'off' | 'annotate' | 'enforce';
  geofenceRadiusM: number;
  minAccuracyM: number;
  payable: 'none' | 'worked_hours' | 'per_day';
  payrollHourType?: string;
  active: boolean;
}

export interface WorkAssignment {
  id: string;
  employeeId: string;
  userId?: string;
  typeKey: string;
  typeLabel: string;
  presenceLabel: string;
  title: string;
  description?: string;
  plannedStartAt?: string;
  plannedEndAt?: string;
  allDay: boolean;
  contextType?: string;
  contextLabel?: string;
  contextSecondary?: string;
  origin?: string;
  destination?: string;
  // The server has always sent these (models.go siteLatitude/siteLongitude/
  // geofenceRadiusM, omitempty); they were simply missing here, so an edit form
  // had no way to show the geofence that was already set.
  siteBranchId?: string;
  siteLatitude?: number;
  siteLongitude?: number;
  geofenceRadiusM?: number;
  status: 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  approvalStatus: 'pending' | 'approved' | 'rejected';
  actualStartAt?: string;
  actualEndAt?: string;
  /** Derived server-side: check-in → check-out, whole minutes. */
  workedMinutes?: number;
  cancelReason?: string;
  source: 'internal' | 'api' | 'external';
  sourceProvider?: string;
  externalUrl?: string;
  approvedBy?: string;
  decisionNote?: string;
  createdAt: string;
}

export interface AssignmentEvidence {
  id: string;
  kind: string;
  occurredAt: string;
  recordedAt: string;
  source: string;
  // Location: the verification enum + distance + coarse address are the
  // manager-view answer, always present when the type captures location. Raw
  // latitude/longitude/accuracyM are precise-gated server-side — only the
  // assignee and tenant-scope viewers receive them.
  verification?: 'NOT_REQUIRED' | 'VERIFIED' | 'OUTSIDE_GEOFENCE' | 'LOW_ACCURACY' | 'LOCATION_UNAVAILABLE';
  distanceM?: number;
  address?: string;
  latitude?: number;
  longitude?: number;
  accuracyM?: number;
  note?: string;
}

// Per-tenant location-capture policy: the floor over each type's gpsPolicy.
export type LocationMode = 'disabled' | 'optional' | 'required';

export interface PresenceDay {
  employeeId: string;
  date: string;
  status: 'present' | 'absent' | 'rest' | 'unverified' | 'missing';
  kind: string;
  kindLabel: string;
  minutes?: number;
  evidence: Record<string, unknown>;
  conflict?: string;
}

export interface PresenceException {
  id: string;
  employeeId: string;
  workDate: string;
  type: string;
  severity: 'info' | 'warning' | 'critical';
  status: 'open' | 'resolved' | 'ignored';
  refs: Record<string, unknown>;
  note?: string;
  createdAt: string;
}

export interface ContextHoursRow {
  contextType: string;
  contextLabel: string;
  typeKey: string;
  assignments: number;
  employees: number;
  hours: number;
}

export const listTypes = async (): Promise<AssignmentType[]> =>
  (await gatewayApi.get<{ types: AssignmentType[] }>(TYPES)).data.types || [];

export const upsertType = async (t: Partial<AssignmentType>): Promise<AssignmentType> =>
  (await gatewayApi.post<AssignmentType>(TYPES, t)).data;

// Tenant location-capture policy (floor over per-type gpsPolicy). Read is
// PermRead; write is PermManage server-side.
export const getLocationPolicy = async (): Promise<LocationMode> =>
  (await gatewayApi.get<{ mode: LocationMode }>(`${TYPES}/location-policy`)).data.mode;

export const setLocationPolicy = async (mode: LocationMode): Promise<LocationMode> =>
  (await gatewayApi.put<{ mode: LocationMode }>(`${TYPES}/location-policy`, { mode })).data.mode;

// Team/all view (managers/HR). `employeeId` is honored ONLY for privileged
// callers server-side; a bare employee is auto-scoped to themselves regardless.
export const listAssignments = async (params: {
  employeeId?: string; status?: string; type?: string; from?: string; to?: string;
  q?: string; limit?: number; offset?: number;
} = {}): Promise<WorkAssignment[]> =>
  (await gatewayApi.get<{ assignments: WorkAssignment[] }>(WA, { params })).data.assignments || [];

// "My Assignments" — the dedicated, always-self-scoped endpoint. The employee is
// resolved from the auth session server-side; role never widens it. NEVER build a
// personal list by loading all assignments and filtering client-side.
export const listMyAssignments = async (params: {
  status?: string; type?: string; from?: string; to?: string;
  q?: string; limit?: number; offset?: number;
} = {}): Promise<WorkAssignment[]> =>
  (await gatewayApi.get<{ assignments: WorkAssignment[] }>(`${WA}/mine`, { params })).data.assignments || [];

/** Count-per-status over the team/admin population (List scope; honors q, ignores status). */
export const assignmentStatusCounts = async (params: {
  employeeId?: string; type?: string; from?: string; to?: string; q?: string;
} = {}): Promise<Record<string, number>> =>
  (await gatewayApi.get<{ counts: Record<string, number> }>(`${WA}/status-counts`, { params })).data.counts || {};

/** Count-per-status for the authenticated employee only (My Assignments). */
export const myAssignmentStatusCounts = async (params: {
  type?: string; from?: string; to?: string; q?: string;
} = {}): Promise<Record<string, number>> =>
  (await gatewayApi.get<{ counts: Record<string, number> }>(`${WA}/mine/status-counts`, { params })).data.counts || {};

export const getAssignment = async (id: string): Promise<{ assignment: WorkAssignment; evidence: AssignmentEvidence[] }> =>
  (await gatewayApi.get<{ assignment: WorkAssignment; evidence: AssignmentEvidence[] }>(`${WA}/${id}`)).data;

export const createAssignment = async (input: Record<string, unknown>): Promise<WorkAssignment> =>
  (await gatewayApi.post<WorkAssignment>(WA, input)).data;

export const pendingApprovals = async (): Promise<WorkAssignment[]> =>
  (await gatewayApi.get<{ assignments: WorkAssignment[] }>(`${WA}/pending-approvals`)).data.assignments || [];

export const approveAssignment = async (id: string, note?: string): Promise<WorkAssignment> =>
  (await gatewayApi.post<WorkAssignment>(`${WA}/${id}/approve`, { note })).data;

export const rejectAssignment = async (id: string, reason: string): Promise<WorkAssignment> =>
  (await gatewayApi.post<WorkAssignment>(`${WA}/${id}/reject`, { reason })).data;

/** Correct the plan of an assignment that has not started yet. Only the keys you
 *  pass are changed — an omitted key means "leave it alone", which is why the
 *  caller must not spread a whole assignment in here. employeeId and typeKey are
 *  not editable: reassigning and retyping are separate acts (server enforces). */
export const updateAssignment = async (
  id: string,
  patch: Record<string, unknown>,
): Promise<WorkAssignment> =>
  (await gatewayApi.patch<WorkAssignment>(`${WA}/${id}`, patch)).data;

export const cancelAssignment = async (id: string, reason: string): Promise<WorkAssignment> =>
  (await gatewayApi.post<WorkAssignment>(`${WA}/${id}/cancel`, { reason })).data;

export const checkIn = async (id: string): Promise<WorkAssignment> =>
  (await gatewayApi.post<WorkAssignment>(`${WA}/${id}/check-in`, { source: 'web' })).data;

export const checkOut = async (id: string): Promise<WorkAssignment> =>
  (await gatewayApi.post<WorkAssignment>(`${WA}/${id}/check-out`, { source: 'web' })).data;

export interface PresenceToday {
  /** The day actually being shown. Not always today — see `is_today`. */
  date: string;
  requested_date: string;
  /** False when nothing had reached us for today and this is the last day we
   *  hold. The screen MUST say so: blank is "we have not been told yet", not
   *  "nobody came to work". */
  is_today: boolean;
  counts: Record<string, number>;
  /** office | outside | away | unverified | unaccounted — grouped server-side
   *  so no two screens invent their own definition of "out". */
  groups: Record<string, number>;
  employees: PresenceDay[];
  /** P2: how current the scanner data is. Absent from an older server. */
  freshness?: PresenceFreshness;
}

export interface PresenceFreshness {
  connected: boolean;
  lastContactAt?: string | null;
  stale: boolean;
  staleAfterMinutes: number;
  coveredThrough?: string;
  unmatchedScannerCodes: number;
  rosterAvailable: boolean;
  degraded?: boolean;
  /** Backup self check-in left on for more than 3 days. */
  backupReminders?: Array<{ scope: 'default' | 'branch'; branchName?: string | null; days: number; by?: string | null }>;
}

export const presenceToday = async (): Promise<PresenceToday> => {
  const { data } = await gatewayApi.get<Partial<PresenceToday> & { employees: PresenceDay[] | null }>(
    `${PRESENCE}/today`,
  );
  const date = data.date || '';
  return {
    date,
    requested_date: data.requested_date || date,
    // An older workassignment-service sends neither field; treat it as today
    // rather than captioning a live board with a warning it does not deserve.
    is_today: data.is_today ?? true,
    counts: data.counts || {},
    groups: data.groups || {},
    employees: data.employees || [],
    freshness: data.freshness,
  };
};

/** The caller's OWN presence. A dedicated route rather than /presence/days,
 *  because reading your own record should not require `attendance:read` —
 *  that permission is the key to the whole tenant's attendance. */
export const myPresence = async (from: string, to: string): Promise<PresenceDay[]> =>
  (await gatewayApi.get<{ days: PresenceDay[] }>(`${PRESENCE}/me`, { params: { from, to } })).data.days || [];

export interface PresenceHistoryQuery {
  from: string;
  to: string;
  employeeId?: string;
  department?: string;
  branch?: string;
  entity?: string;
  source?: string;
  status?: string;
  presence?: string;
  exceptionsOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface PresenceHistoryPage {
  days: PresenceDay[];
  total: number;
  limit: number;
  offset: number;
}

/** History, filtered and paged SERVER-side (P2) — never filtered in React. */
export const presenceHistory = async (q: PresenceHistoryQuery): Promise<PresenceHistoryPage> => {
  const params: Record<string, string | number> = { from: q.from, to: q.to };
  (['employeeId', 'department', 'branch', 'entity', 'source', 'status', 'presence'] as const)
    .forEach((k) => { if (q[k]) params[k] = q[k] as string; });
  if (q.exceptionsOnly) params.exceptionsOnly = 'true';
  params.limit = q.limit ?? 100;
  params.offset = q.offset ?? 0;
  const { data } = await gatewayApi.get<Partial<PresenceHistoryPage>>(`${PRESENCE}/days`, { params });
  const days = data.days || [];
  return { days, total: data.total ?? days.length, limit: data.limit ?? days.length, offset: data.offset ?? 0 };
};

export const presenceDays = async (from: string, to: string, employeeId?: string): Promise<PresenceDay[]> =>
  (await presenceHistory({ from, to, employeeId, limit: 500 })).days;

export const presenceExceptions = async (status = 'open'): Promise<PresenceException[]> =>
  (await gatewayApi.get<{ exceptions: PresenceException[] }>(`${PRESENCE}/exceptions`, { params: { status } })).data.exceptions || [];

export const resolvePresenceException = async (id: string, status: 'resolved' | 'ignored', note?: string) =>
  (await gatewayApi.post(`${PRESENCE}/exceptions/${id}/resolve`, { status, note })).data;

export const contextHours = async (from: string, to: string): Promise<ContextHoursRow[]> =>
  (await gatewayApi.get<{ rows: ContextHoursRow[] }>(`${WA}/report/context-hours`, { params: { from, to } })).data.rows || [];

/** Manual attendance correction (leave-service; RC-5 backend from Phase 1). */
export const correctAttendanceDay = async (input: {
  employee_id: string; work_date: string; clock_in?: string; clock_out?: string;
  net_minutes?: number; day_kind?: string; reason: string;
}): Promise<{ status: string; applied?: boolean; reason?: string }> =>
  (await gatewayApi.post('/api/v1/attendance/days/correct', input)).data;

// Presence kind → label + token-safe badge classes (KaziPeople global theme:
// semantic colors only through tokens; muted is a SURFACE, never a text color).
export const PRESENCE_BADGE: Record<string, string> = {
  office: 'bg-info-soft text-info-strong',
  client_site: 'bg-accent text-accent-foreground',
  field: 'bg-accent text-accent-foreground',
  route: 'bg-accent text-accent-foreground',
  travel: 'bg-accent text-accent-foreground',
  training: 'bg-accent text-accent-foreground',
  remote: 'bg-muted text-muted-foreground',
  leave: 'bg-muted text-foreground',
  holiday: 'bg-muted text-foreground',
  rest_day: 'bg-muted text-foreground',
  unknown: 'bg-danger-soft text-danger-strong',
};
